/**
 * Transient blob: display URL helpers.
 * Durable IndexedDB records store Blobs only — never these URLs.
 * Consumers own lifecycle via retain/release (see useRevokeObjectUrls).
 */

export interface ObjectUrlApi {
  createObjectURL: (blob: Blob) => string;
  revokeObjectURL: (url: string) => void;
}

const defaultApi: ObjectUrlApi = {
  createObjectURL: (blob) => URL.createObjectURL(blob),
  revokeObjectURL: (url) => {
    URL.revokeObjectURL(url);
  },
};

let objectUrlApi: ObjectUrlApi = defaultApi;

/** Injectable seam for tests. */
export function setObjectUrlApiForTests(api: ObjectUrlApi | null): void {
  objectUrlApi = api ?? defaultApi;
}

export function createDisplayObjectUrl(blob: Blob): string {
  return objectUrlApi.createObjectURL(blob);
}

export function revokeDisplayObjectUrl(url: string): void {
  try {
    objectUrlApi.revokeObjectURL(url);
  } catch {
    /* ignore */
  }
}

export function isBlobObjectUrl(url: string | null | undefined): url is string {
  return typeof url === 'string' && url.startsWith('blob:');
}

/** Deduplicate blob: URLs from mixed display fields. */
export function collectBlobObjectUrls(
  values: Iterable<string | null | undefined>,
): string[] {
  const out = new Set<string>();
  for (const value of values) {
    if (isBlobObjectUrl(value)) out.add(value);
  }
  return [...out];
}

export function revokeDisplayObjectUrls(
  values: Iterable<string | null | undefined>,
): void {
  for (const url of collectBlobObjectUrls(values)) {
    revokeDisplayObjectUrl(url);
  }
}

/**
 * Ref-count retain/release so two mounted consumers sharing a URL (or React
 * Strict Mode retain/release pairs) do not revoke while still in use.
 */
const retainCounts = new Map<string, number>();

export function retainDisplayObjectUrl(url: string): void {
  if (!isBlobObjectUrl(url)) return;
  retainCounts.set(url, (retainCounts.get(url) ?? 0) + 1);
}

export function releaseDisplayObjectUrl(url: string): void {
  if (!isBlobObjectUrl(url)) return;
  const next = (retainCounts.get(url) ?? 0) - 1;
  if (next <= 0) {
    retainCounts.delete(url);
    revokeDisplayObjectUrl(url);
  } else {
    retainCounts.set(url, next);
  }
}

export function retainDisplayObjectUrls(
  values: Iterable<string | null | undefined>,
): string[] {
  const urls = collectBlobObjectUrls(values);
  for (const url of urls) retainDisplayObjectUrl(url);
  return urls;
}

export function releaseDisplayObjectUrls(
  values: Iterable<string | null | undefined>,
): void {
  for (const url of collectBlobObjectUrls(values)) {
    releaseDisplayObjectUrl(url);
  }
}

export interface DisplayObjectUrlOwner {
  /** Reconcile this consumer's ownership without releasing intersections. */
  replace(values: Iterable<string | null | undefined>): void;
  /** Release every URL still owned by this consumer. */
  releaseAll(): void;
  /** Snapshot used by deterministic lifecycle tests. */
  current(): string[];
}

/**
 * Isolated ownership for one rendering consumer. Replacements are diffed:
 * additions are retained, removals are released, and intersections remain
 * continuously retained.
 */
export function createDisplayObjectUrlOwner(): DisplayObjectUrlOwner {
  let owned = new Set<string>();

  return {
    replace(values) {
      const next = new Set(collectBlobObjectUrls(values));
      for (const url of next) {
        if (!owned.has(url)) retainDisplayObjectUrl(url);
      }
      for (const url of owned) {
        if (!next.has(url)) releaseDisplayObjectUrl(url);
      }
      owned = next;
    },

    releaseAll() {
      for (const url of owned) releaseDisplayObjectUrl(url);
      owned = new Set();
    },

    current() {
      return [...owned];
    },
  };
}

/** Test helpers */
export function resetObjectUrlLifecycleForTests(): void {
  for (const url of [...retainCounts.keys()]) {
    revokeDisplayObjectUrl(url);
  }
  retainCounts.clear();
  objectUrlApi = defaultApi;
}

export function countRetainedObjectUrlsForTests(): number {
  return retainCounts.size;
}

export function retainCountForTests(url: string): number {
  return retainCounts.get(url) ?? 0;
}
